import { runtimeConfig } from '../../config/runtime';
import type {
  ActiveContext,
  Business,
  AuthProvider,
  AuthResult,
  AuthSession,
  AuthUser,
  OnboardingPayload,
  Workspace,
} from './types';

const SESSION_KEY = 'nexo.phase2.supabase.session';

type SupabaseUser = {
  id: string;
  email?: string;
  email_confirmed_at?: string | null;
  user_metadata?: Record<string, unknown>;
};

type SupabaseAuthResponse = {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  user?: SupabaseUser;
  error_description?: string;
  msg?: string;
};

const authHeaders = () => ({
  apikey: runtimeConfig.supabaseAnonKey,
  Authorization: `Bearer ${runtimeConfig.supabaseAnonKey}`,
  'Content-Type': 'application/json',
});

const apiError = async (response: Response) => {
  const payload = await response.json().catch(() => ({})) as Record<string, unknown>;
  const message = String(payload.msg ?? payload.error_description ?? payload.message ?? 'No se pudo completar la operación.');
  throw new Error(message);
};

const mapUser = (user: SupabaseUser): AuthUser => ({
  id: user.id,
  email: user.email ?? '',
  firstName: String(user.user_metadata?.first_name ?? ''),
  lastName: String(user.user_metadata?.last_name ?? ''),
  emailVerified: Boolean(user.email_confirmed_at),
});

const mapSession = (payload: SupabaseAuthResponse): AuthSession => {
  if (!payload.access_token || !payload.user) throw new Error('Supabase no devolvió una sesión válida.');
  return {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresAt: Date.now() + Number(payload.expires_in ?? 3600) * 1000,
    provider: 'supabase',
    user: mapUser(payload.user),
  };
};


const enrichSessionPlatformRole = async (session: AuthSession): Promise<AuthSession> => {
  const response = await fetch(
    `${runtimeConfig.supabaseUrl}/rest/v1/app_admins?select=role,active&user_id=eq.${session.user.id}&active=eq.true&limit=1`,
    {
      headers: {
        apikey: runtimeConfig.supabaseAnonKey,
        Authorization: `Bearer ${session.accessToken}`,
      },
    },
  );

  if (!response.ok) return session;

  const rows = await response.json() as Array<{ role?: 'superadmin' | 'support'; active?: boolean }>;
  const role = rows[0]?.active ? rows[0]?.role : undefined;
  return role ? { ...session, user: { ...session.user, platformRole: role } } : session;
};

const saveSession = (session: AuthSession | null) => {
  if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  else localStorage.removeItem(SESSION_KEY);
};


const uploadBusinessLogo = async (session: AuthSession, logoDataUrl: string) => {
  if (!logoDataUrl.startsWith('data:')) return logoDataUrl;
  const match = logoDataUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) return logoDataUrl;
  const mimeType = match[1];
  const extension = mimeType.includes('svg') ? 'svg' : mimeType.includes('jpeg') ? 'jpg' : 'png';
  const bytes = Uint8Array.from(atob(match[2]), (character) => character.charCodeAt(0));
  const objectPath = `${session.user.id}/${Date.now()}.${extension}`;
  const response = await fetch(`${runtimeConfig.supabaseUrl}/storage/v1/object/business-branding/${objectPath}`, {
    method: 'POST',
    headers: {
      apikey: runtimeConfig.supabaseAnonKey,
      Authorization: `Bearer ${session.accessToken}`,
      'Content-Type': mimeType,
      'x-upsert': 'false',
    },
    body: bytes,
  });
  if (!response.ok) return apiError(response);
  return `${runtimeConfig.supabaseUrl}/storage/v1/object/public/business-branding/${objectPath}`;
};


const enrichWorkspaceProfiles = async (session: AuthSession, workspace: Workspace): Promise<Workspace> => {
  const ids = workspace.businesses.map((business) => business.id);
  if (ids.length === 0) return workspace;
  const response = await fetch(`${runtimeConfig.supabaseUrl}/rest/v1/businesses?select=id,profile_id,operation_model,cash_control_enabled&id=in.(${ids.join(',')})`, {
    headers: { apikey: runtimeConfig.supabaseAnonKey, Authorization: `Bearer ${session.accessToken}` },
  });
  if (!response.ok) return workspace;
  const rows = await response.json() as Array<{ id: string; profile_id?: string; operation_model?: string; cash_control_enabled?: boolean }>;
  return {
    ...workspace,
    businesses: workspace.businesses.map((business) => { const row = rows.find((item) => item.id === business.id); return ({ ...business, profileId: (row?.profile_id ?? business.profileId ?? 'custom') as Business['profileId'], operationModel: (row?.operation_model ?? business.operationModel ?? 'physical-store') as Business['operationModel'], cashControlEnabled: row?.cash_control_enabled ?? business.cashControlEnabled ?? true }); }),
  };
};

const updateBusinessProfile = async (session: AuthSession, businessId: string, profileId: Business['profileId']) => {
  const response = await fetch(`${runtimeConfig.supabaseUrl}/rest/v1/businesses?id=eq.${businessId}`, {
    method: 'PATCH',
    headers: { apikey: runtimeConfig.supabaseAnonKey, Authorization: `Bearer ${session.accessToken}`, 'Content-Type': 'application/json', Prefer: 'return=minimal' },
    body: JSON.stringify({ profile_id: profileId }),
  });
  if (!response.ok) return apiError(response);
};

const rpc = async <T>(session: AuthSession, name: string, body: Record<string, unknown> = {}): Promise<T> => {
  const response = await fetch(`${runtimeConfig.supabaseUrl}/rest/v1/rpc/${name}`, {
    method: 'POST',
    headers: {
      apikey: runtimeConfig.supabaseAnonKey,
      Authorization: `Bearer ${session.accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) return apiError(response);
  return response.json() as Promise<T>;
};

export class SupabaseAuthProvider implements AuthProvider {
  mode = 'supabase' as const;

  async refreshSession(stored: AuthSession): Promise<AuthSession> {
    if (!stored.refreshToken) {
      saveSession(null);
      throw new Error('Tu sesión venció. Inicia sesión nuevamente.');
    }
    const response = await fetch(`${runtimeConfig.supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ refresh_token: stored.refreshToken }),
    });
    if (!response.ok) {
      saveSession(null);
      throw new Error('Tu sesión venció. Inicia sesión nuevamente.');
    }
    const session = await enrichSessionPlatformRole(mapSession(await response.json() as SupabaseAuthResponse));
    saveSession(session);
    return session;
  }

  async restoreSession(): Promise<AuthSession | null> {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    try {
      const stored = JSON.parse(raw) as AuthSession;
      if (stored.expiresAt > Date.now() + 2 * 60_000) {
        const session = await enrichSessionPlatformRole(stored);
        saveSession(session);
        return session;
      }
      return await this.refreshSession(stored);
    } catch {
      saveSession(null);
      return null;
    }
  }

  async signIn(email: string, password: string): Promise<AuthResult> {
    const response = await fetch(`${runtimeConfig.supabaseUrl}/auth/v1/token?grant_type=password`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ email, password }),
    });
    if (!response.ok) return apiError(response);
    const session = await enrichSessionPlatformRole(mapSession(await response.json() as SupabaseAuthResponse));
    saveSession(session);
    return { session };
  }

  async signUp(input: { email: string; password: string; firstName: string; lastName: string }): Promise<AuthResult> {
    const response = await fetch(`${runtimeConfig.supabaseUrl}/auth/v1/signup`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ email: input.email, password: input.password, data: { first_name: input.firstName, last_name: input.lastName } }),
    });
    if (!response.ok) return apiError(response);
    const payload = await response.json() as SupabaseAuthResponse;
    if (payload.access_token) {
      const session = await enrichSessionPlatformRole(mapSession(payload));
      saveSession(session);
      return { session };
    }
    throw new Error('Supabase creó la cuenta sin sesión. Desactiva Confirm email en Authentication → Providers → Email.');
  }

  async signOut(session: AuthSession): Promise<void> {
    await fetch(`${runtimeConfig.supabaseUrl}/auth/v1/logout`, {
      method: 'POST',
      headers: { apikey: runtimeConfig.supabaseAnonKey, Authorization: `Bearer ${session.accessToken}` },
    }).catch(() => undefined);
    saveSession(null);
  }

  async requestMagicLink(email: string): Promise<{ message: string }> {
    const redirectTo = encodeURIComponent(`${window.location.origin}/auth/callback`);
    const response = await fetch(`${runtimeConfig.supabaseUrl}/auth/v1/otp?redirect_to=${redirectTo}`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ email, create_user: false }),
    });
    if (!response.ok) return apiError(response);
    return { message: 'Revisa tu correo para abrir el enlace de acceso.' };
  }

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const redirectTo = encodeURIComponent(`${window.location.origin}/auth/callback`);
    const response = await fetch(`${runtimeConfig.supabaseUrl}/auth/v1/recover?redirect_to=${redirectTo}`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ email }),
    });
    if (!response.ok) return apiError(response);
    return { message: 'Revisa tu correo para restablecer la contraseña.' };
  }

  async confirmEmail(): Promise<AuthSession> {
    throw new Error('La confirmación se procesa mediante el enlace enviado por Supabase.');
  }

  async consumeMagicLink(): Promise<AuthSession> {
    throw new Error('El enlace mágico se procesa mediante Supabase Auth.');
  }

  async resetPassword(token: string, password: string): Promise<void> {
    const response = await fetch(`${runtimeConfig.supabaseUrl}/auth/v1/user`, {
      method: 'PUT',
      headers: { apikey: runtimeConfig.supabaseAnonKey, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!response.ok) return apiError(response);
  }

  async consumeCallback(params: Record<string, string>): Promise<AuthSession> {
    const accessToken = params.access_token;
    if (!accessToken) throw new Error('Supabase no devolvió un token de acceso.');
    const response = await fetch(`${runtimeConfig.supabaseUrl}/auth/v1/user`, {
      headers: { apikey: runtimeConfig.supabaseAnonKey, Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) return apiError(response);
    const user = await response.json() as SupabaseUser;
    const session = await enrichSessionPlatformRole({
      accessToken,
      refreshToken: params.refresh_token,
      expiresAt: Date.now() + Number(params.expires_in ?? 3600) * 1000,
      provider: 'supabase',
      user: mapUser(user),
    });
    saveSession(session);
    return session;
  }

  async loadWorkspace(session: AuthSession): Promise<Workspace> {
    return enrichWorkspaceProfiles(session, await rpc<Workspace>(session, 'get_user_workspace'));
  }

  async completeOnboarding(session: AuthSession, payload: OnboardingPayload): Promise<Workspace> {
    const logoDataUrl = payload.business.logoDataUrl ? await uploadBusinessLogo(session, payload.business.logoDataUrl) : '';
    const enriched = { ...payload, business: { ...payload.business, logoDataUrl } };
    const workspace = await rpc<Workspace>(session, 'complete_onboarding', { payload: enriched });
    const createdBusiness = workspace.businesses[0];
    if (createdBusiness) await updateBusinessProfile(session, createdBusiness.id, payload.business.profileId);
    return { ...workspace, businesses: workspace.businesses.map((business) => ({ ...business, profileId: business.id === createdBusiness?.id ? payload.business.profileId : business.profileId ?? 'custom' })) };
  }

  async switchContext(session: AuthSession, context: ActiveContext): Promise<Workspace> {
    return rpc<Workspace>(session, 'switch_active_context', { selected_context: context });
  }
}
